import type { ProjectMember } from '../../auth/types';

export interface MemberWithEmail extends ProjectMember {
    email: string;
    displayName: string;
    isSuperAdmin?: boolean;
}

export type StoredProjectMember = Omit<ProjectMember, 'userId' | 'joinedAt' | 'updatedAt'> & {
    userId?: unknown;
    joinedAt: unknown;
    updatedAt?: unknown;
};

type UserProfileData = {
    email?: unknown;
    displayName?: unknown;
    isSuperAdmin?: unknown;
};

type TimestampLike = {
    toMillis?: () => number;
    toDate?: () => Date;
    seconds?: number;
    nanoseconds?: number;
};

type ProjectMemberDate = ProjectMember['joinedAt'] | ProjectMember['updatedAt'];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finiteNumber(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function timestampLikeToMillis(value: unknown): number | undefined {
    if (!isRecord(value)) return;

    const timestamp = value as TimestampLike;
    if (typeof timestamp.toMillis === 'function') {
        return finiteNumber(timestamp.toMillis());
    }

    if (typeof timestamp.toDate === 'function') {
        return finiteNumber(timestamp.toDate().getTime());
    }

    if (typeof timestamp.seconds === 'number') {
        const nanoseconds = typeof timestamp.nanoseconds === 'number' ? timestamp.nanoseconds : 0;
        return finiteNumber((timestamp.seconds * 1000) + Math.floor(nanoseconds / 1_000_000));
    }

    return;
}

function normalizeMemberDate(value: unknown): ProjectMemberDate {
    return timestampLikeToMillis(value) ?? (value as ProjectMemberDate);
}

function getStoredUserId(memberData: StoredProjectMember): string | undefined {
    return typeof memberData.userId === 'string' && memberData.userId.trim()
        ? memberData.userId
        : undefined;
}

export function normalizeMemberWithProfile(
    memberDocId: string,
    memberData: StoredProjectMember,
    userProfile: UserProfileData | null
): MemberWithEmail {
    return {
        ...memberData,
        userId: getStoredUserId(memberData) ?? memberDocId,
        joinedAt: normalizeMemberDate(memberData.joinedAt) as ProjectMember['joinedAt'],
        updatedAt: memberData.updatedAt === undefined
            ? undefined
            : normalizeMemberDate(memberData.updatedAt),
        email: typeof userProfile?.email === 'string' ? userProfile.email : 'Unknown',
        displayName: typeof userProfile?.displayName === 'string' ? userProfile.displayName : 'Unknown User',
        isSuperAdmin: userProfile?.isSuperAdmin === true,
    };
}
